import type { AppState, LogEntry } from './state';
import { getSettings, putSettings, type HBFDatabase } from './db';

export interface ExportContribution {
  date: string;
  amount_cents: string;
  institution?: string;
  created_at: string;
}

export interface ExportWithdrawal {
  date: string;
  amount_cents: string;
  institution?: string;
  created_at: string;
}

export interface ExportSettings {
  birth_year: number;
  residency_year: string;
  cra_room_method: string;
  cra_room_initial_cents: string;
  cra_verified_at_iso: string | null;
  contributions_ytd_cents: string;
  withdrawals_ytd_cents: string;
  disclaimer_accepted_at: string | null;
  wizard_completed_at: string | null;
}

export interface ExportPayload {
  format: 'hbf-tfsa-export';
  format_version: 1;
  exported_at: string;
  tax_year: number;
  constants_version: string;
  settings: ExportSettings;
  contributions: ExportContribution[];
  withdrawals: ExportWithdrawal[];
}

export interface EncryptedExport {
  format: 'hbf-tfsa-export-encrypted';
  format_version: 1;
  algorithm: 'AES-256-GCM';
  kdf: 'PBKDF2-SHA256-600000';
  salt: string;       // base64url
  iv: string;         // base64url
  ciphertext: string; // base64url
}

export function buildExportPayload(state: AppState): ExportPayload {
  const w = state.wizardInputs;
  if (!w) throw new Error('Cannot export: wizard not completed');

  const contributions: ExportContribution[] = state.logEntries
    .filter((e): e is LogEntry & { type: 'contribution' } => e.type === 'contribution')
    .map(({ date, amount_cents, institution, created_at }) => ({
      date,
      amount_cents,
      ...(institution !== undefined ? { institution } : {}),
      created_at,
    }));

  const withdrawals: ExportWithdrawal[] = state.logEntries
    .filter((e): e is LogEntry & { type: 'withdrawal' } => e.type === 'withdrawal')
    .map(({ date, amount_cents, institution, created_at }) => ({
      date,
      amount_cents,
      ...(institution !== undefined ? { institution } : {}),
      created_at,
    }));

  return {
    format: 'hbf-tfsa-export',
    format_version: 1,
    exported_at: new Date().toISOString(),
    tax_year: 2026,
    constants_version: state.constantsVersion,
    settings: {
      birth_year: w.birth_year,
      residency_year: String(w.residency_year),
      cra_room_method: w.cra_room_method,
      cra_room_initial_cents: w.cra_room_initial_cents,
      cra_verified_at_iso: w.cra_verified_at_iso,
      contributions_ytd_cents: w.contributions_ytd_cents,
      withdrawals_ytd_cents: w.withdrawals_ytd_cents,
      disclaimer_accepted_at: state.disclaimerAccepted ? new Date().toISOString() : null,
      wizard_completed_at: state.wizardCompleted ? new Date().toISOString() : null,
    },
    contributions,
    withdrawals,
  };
}

export function serializePlainExport(payload: ExportPayload): string {
  return JSON.stringify(payload, null, 2);
}

export function downloadAsFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function exportFilename(encrypted: boolean): string {
  const date = new Date().toISOString().split('T')[0];
  return encrypted ? `hbf-tfsa-${date}.enc.json` : `hbf-tfsa-${date}.json`;
}

// ── AES-256-GCM helpers ──────────────────────────────────────────────────────

function toBase64url(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function fromBase64url(str: string): ArrayBuffer {
  const b64 = str.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}

async function deriveKey(passphrase: string, salt: ArrayBuffer): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const baseKey = await crypto.subtle.importKey('raw', enc.encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: 600_000, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function encryptExport(plaintext: string, passphrase: string): Promise<EncryptedExport> {
  const salt = crypto.getRandomValues(new Uint8Array(32)).buffer;
  const iv = crypto.getRandomValues(new Uint8Array(12)).buffer;
  const key = await deriveKey(passphrase, salt);
  const enc = new TextEncoder();
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(plaintext));
  return {
    format: 'hbf-tfsa-export-encrypted',
    format_version: 1,
    algorithm: 'AES-256-GCM',
    kdf: 'PBKDF2-SHA256-600000',
    salt: toBase64url(salt),
    iv: toBase64url(iv),
    ciphertext: toBase64url(ciphertext),
  };
}

export async function decryptExport(encrypted: EncryptedExport, passphrase: string): Promise<string> {
  const salt = fromBase64url(encrypted.salt);
  const iv = fromBase64url(encrypted.iv);
  const ciphertext = fromBase64url(encrypted.ciphertext);
  const key = await deriveKey(passphrase, salt);
  const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
  return new TextDecoder().decode(plaintext);
}

export async function recordExportInDb(db: HBFDatabase): Promise<void> {
  const current = await getSettings(db);
  if (current) {
    await putSettings(db, {
      ...current,
      last_backup_export_at: new Date().toISOString(),
      contributions_since_last_backup: 0,
    });
  }
}
